/**
 * The morning alert's switch: new matches by email, each morning, for the
 * people who ask. Off by default. Honest about delivery: until the sender
 * is configured on the server, the switch says so rather than promising.
 */

import { useEffect, useState } from "react";
import { api, ApiError } from "./auth";

interface AlertsState {
  enabled: boolean;
  email: string | null;
  lastSentAt: string | null;
  configured: boolean;
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.messages.join(" ");
  return err instanceof Error ? err.message : String(err);
}

export function AlertsSwitch() {
  const [state, setState] = useState<AlertsState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nowBusy, setNowBusy] = useState(false);
  const [nowMsg, setNowMsg] = useState<string | null>(null);

  useEffect(() => {
    api<AlertsState>("/api/alerts").then(setState).catch((err) => setError(describe(err)));
  }, []);

  const toggle = (enabled: boolean) => {
    if (!state || busy) return;
    setBusy(true);
    setError(null);
    api<{ enabled: boolean; configured: boolean }>("/api/alerts", { method: "PUT", body: JSON.stringify({ enabled }) })
      .then((r) => setState({ ...state, enabled: r.enabled, configured: r.configured }))
      .catch((err) => setError(describe(err)))
      .finally(() => setBusy(false));
  };

  const sendOneNow = () => {
    if (!state || nowBusy) return;
    setNowBusy(true);
    setNowMsg(null);
    setError(null);
    api<{ sent: boolean; nothing: boolean }>("/api/alerts/send-now", { method: "POST" })
      .then((r) => setNowMsg(r.sent ? "Sent — check your inbox." : "No matches to send right now."))
      .catch((err) => setError(describe(err)))
      .finally(() => setNowBusy(false));
  };

  if (!state && !error) return null;

  return (
    <div className="alerts">
      <label className="alerts-row">
        <input
          type="checkbox"
          checked={state?.enabled ?? false}
          disabled={busy || !state}
          onChange={(e) => toggle(e.target.checked)}
        />
        <span>
          Email me new matches and due follow-ups each morning
          {state?.email ? <span className="muted small"> — to {state.email}</span> : null}
        </span>
      </label>
      {state?.enabled && !state.configured && (
        <p className="muted small alerts-note">Saved. Sending is not switched on yet on our side; you will get the first one once it is.</p>
      )}
      {state?.enabled && state.configured && state.lastSentAt && (
        <p className="muted small alerts-note">Last checked {new Date(state.lastSentAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}.</p>
      )}
      {state?.configured && (
        <div className="alerts-note">
          <button type="button" className="btn ghost small" onClick={sendOneNow} disabled={nowBusy || !state}>
            {nowBusy ? "Sending…" : "Email me my matches now"}
          </button>
          {nowMsg && <span className="muted small" style={{ marginLeft: 8 }}>{nowMsg}</span>}
        </div>
      )}
      {error && <p className="err small">{error}</p>}
    </div>
  );
}
